import { useEffect, useState } from 'react'
import {
  Alert, Button, Code, Group, List, Loader, Modal, Stack, Text, TextInput,
} from '@mantine/core'
import { useMutation, useQuery } from '@tanstack/react-query'
import { deleteWaf, type DeleteWafResponse } from '@/api/waf'
import { getChangeControlPreview } from '@/api/changeControl'
import { formatApiError } from '@/api/errors'

export interface WafProtegido {
  resource_type?: string | null
  resource_arn?: string | null
  name?: string | null
  dns?: string | null
}

export interface WafDeleteModalProps {
  opened: boolean
  onClose: () => void
  wafId: number
  /** Nome do Web ACL — é ele que o operador tem de digitar. */
  wafName: string
  tenantId: number | null
  /** Recursos protegidos segundo o registro. Ver a nota de cache abaixo. */
  recursos?: WafProtegido[]
  onSolicitado?: (r: DeleteWafResponse) => void
}

/**
 * Confirmação da exclusão de um WAF gerenciado.
 *
 * Duas coisas fazem esta tela, e nenhuma é enfeite:
 *
 * 1. **O nome digitado.** É o que impede o clique de inércia numa ação
 *    irreversível. A comparação é EXATA — normalizar caixa ou espaço
 *    enfraqueceria justamente a barreira que se quer.
 * 2. **A lista de recursos que ficarão desprotegidos.** É ela que transforma a
 *    trava em informação: o operador não confirma no escuro.
 *
 * O rótulo do botão vem do gate (`change-control/preview`), como o wizard de
 * provisionamento já faz — anunciar GMUD por engano é pior que não anunciar.
 */
export function WafDeleteModal({
  opened, onClose, wafId, wafName, tenantId, recursos, onSolicitado,
}: WafDeleteModalProps) {
  const [digitado, setDigitado] = useState('')
  const [motivo, setMotivo] = useState('')
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => {
    if (opened) { setDigitado(''); setMotivo(''); setErro(null) }
  }, [opened])

  const gate = useQuery({
    queryKey: ['change-control-preview', 'waf_delete', tenantId],
    queryFn: () => getChangeControlPreview(Number(tenantId), 'waf_delete'),
    enabled: opened && !!tenantId,
    staleTime: 30_000,
  })
  const exigeGmud = gate.data?.requires_gmud
  const rotulo = exigeGmud === true
    ? 'Enviar para GMUD'
    : exigeGmud === false ? 'Excluir agora' : 'Enviar pedido'

  const mut = useMutation({
    mutationFn: () => deleteWaf(wafId, {
      confirm_name: digitado, reason: motivo || null,
    }),
    onSuccess: (r) => { onSolicitado?.(r); onClose() },
    onError: (e) => setErro(formatApiError(e, 'Falha ao registrar a exclusão.')),
  })

  const confere = digitado === wafName
  const lista = recursos ?? []

  return (
    <Modal opened={opened} onClose={onClose} title={`Excluir ${wafName}`} size="lg">
      <Stack gap="md">
        <Alert color="red" title="Esta ação é irreversível">
          <Text size="sm">
            O Web ACL e os IP sets são apagados na AWS. Recriar depois gera um
            recurso com <strong>id novo</strong> e <strong>sem a lista de
            bloqueio</strong> — os endereços bloqueados e o histórico de operação
            ficam apenas no evento de auditoria.
          </Text>
        </Alert>

        <div>
          <Text size="sm" fw={600} mb={4}>
            Ficarão desprotegidos ({lista.length})
          </Text>
          {lista.length === 0 ? (
            <Text size="sm" c="dimmed">
              Nenhum recurso associado segundo o registro.
            </Text>
          ) : (
            <List size="sm" spacing={2}>
              {lista.map((r, i) => (
                <List.Item key={r.resource_arn ?? i}>
                  <Text size="sm" span>{r.name || r.resource_arn}</Text>
                  {r.resource_type ? (
                    <Text size="xs" c="dimmed" span> · {r.resource_type}</Text>
                  ) : null}
                </List.Item>
              ))}
            </List>
          )}
          {/*
            A lista vem do registro (cache de descoberta), nao da AWS na hora.
            Para acao destrutiva, SUBESTIMAR o impacto e o erro perigoso — entao
            o texto diz o que a lista e, em vez de deixar o operador supor que ela
            e definitiva. A execucao consulta a AWS e desassocia tudo que achar.
          */}
          <Text size="xs" c="dimmed" mt={4}>
            Última descoberta conhecida. A exclusão consulta a AWS na hora e
            desassocia todos os recursos que encontrar.
          </Text>
        </div>

        {exigeGmud === true ? (
          <Alert color="blue" title="Esta mudança passa por GMUD">
            <Text size="sm">
              O pedido é registrado e só executa após a aprovação do comitê.
            </Text>
          </Alert>
        ) : null}

        <TextInput
          label="Para confirmar, digite o nome do Web ACL"
          description={<>Exatamente como está: <Code>{wafName}</Code></>}
          value={digitado}
          onChange={(e) => setDigitado(e.currentTarget.value)}
          error={digitado.length > 0 && !confere ? 'O nome não confere.' : null}
          data-autofocus
        />

        <TextInput
          label="Motivo (opcional)"
          description="Vai para a auditoria e para a GMUD."
          value={motivo}
          onChange={(e) => setMotivo(e.currentTarget.value)}
        />

        {erro ? <Alert color="red">{erro}</Alert> : null}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button
            color="red"
            disabled={!confere || mut.isPending}
            onClick={() => mut.mutate()}
            leftSection={mut.isPending ? <Loader size="xs" color="white" /> : null}
          >
            {rotulo}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
